import { useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Collapse, Text, Title } from '@mantine/core';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './WizardCostBreakdown.module.css';
import { resolveChargeLabel, wizardCostBreakdownMessages } from '../../../../ltl/charge-labels';

import type { LtlQuote } from '../../../../types/ltl';

type WizardCostBreakdownProps = {
  selectedRate: LtlQuote | null;
};

export const WizardCostBreakdown = ({ selectedRate }: WizardCostBreakdownProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  const charges = selectedRate?.charges ?? [];
  const hasCharges = charges.length > 0;

  const totalDisplay = selectedRate === null ? '$-' : intl.formatNumber(selectedRate.totalCharges, { style: 'currency', currency: 'USD' });

  return (
    <div aria-label={intl.formatMessage(wizardCostBreakdownMessages.title)} className={styles.costBreakdownSection} role='region'>
      <Collapse in={isExpanded}>
        <div className={styles.chargesList}>
          {charges.map((charge, index) => (
            <div className={styles.chargeRow} data-testid={`cost-breakdown-charge-${charge.code}`} key={`${charge.code}-${index}`}>
              <Text className={styles.chargeLabel} size='sm'>
                {resolveChargeLabel(charge, intl)}
              </Text>
              <Text className={styles.chargePrice} size='sm'>
                {intl.formatNumber(charge.price, { style: 'currency', currency: 'USD' })}
              </Text>
            </div>
          ))}
        </div>
      </Collapse>

      <div className={styles.totalRow}>
        <Title lh='md' order={3}>
          <FormattedMessage {...wizardCostBreakdownMessages.total} />
        </Title>
        <div className={styles.totalRight}>
          <Title lh='md' order={3}>
            {totalDisplay}
          </Title>
          <ActionIcon
            aria-expanded={isExpanded}
            aria-label={intl.formatMessage(wizardCostBreakdownMessages.toggleDetails)}
            className={styles.toggleButton}
            data-disabled={!hasCharges || undefined}
            onClick={() => hasCharges && setIsExpanded((prev) => !prev)}
            variant='ghost'
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </ActionIcon>
        </div>
      </div>
    </div>
  );
};
